import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Briefcase, Users, CheckCircle2, DollarSign, Receipt, Calendar, TrendingUp, Activity } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { useAuth } from "../lib/auth";

const icon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41],
});

function Stat({ label, value, icon: Icon, accent, testid }) {
  return (
    <Card className="rounded-3xl p-6 border-border/60 glass hover:-translate-y-1 transition-all duration-300" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${accent}`}><Icon className="w-5 h-5" /></div>
        <TrendingUp className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="text-3xl font-heading font-bold">{value}</div>
      <div className="text-sm text-muted-foreground mt-1">{label}</div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [jobs, setJobs] = useState([]);
  const [earnings, setEarnings] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({data}) => setStats(data)).catch(()=>{});
    api.get("/jobs").then(({data}) => setJobs(data)).catch(()=>{});
    if (user?.role !== "admin") {
      api.get("/my/earnings").then(({data}) => setEarnings(data)).catch(()=>{});
    }
  }, [user]);

  const markers = jobs.filter(j => j.lat && j.lng);
  const center = markers[0] ? [markers[0].lat, markers[0].lng] : [52.52, 13.405];

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="dashboard-page">
      <div>
        <div className="text-xs text-muted-foreground font-bold uppercase tracking-[0.2em]">
          {user?.role === "admin" ? "Agentur · Dashboard" : user?.role === "customer" ? "Kunde · Übersicht" : "Mein Bereich"}
        </div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">Hallo, {user?.name?.split(" ")[0]}</h1>
        <p className="text-muted-foreground mt-1">
          {user?.role === "admin"
            ? "Hier ist ein Überblick über Ihr Team und Ihre Einsätze."
            : user?.role === "customer"
            ? "Ihre gebuchten Einsätze und Rechnungen."
            : "Ihre Einsätze, Zeiten und Einnahmen auf einen Blick."}
        </p>
      </div>

      {/* Tech/Customer earnings banner */}
      {earnings && user?.role !== "admin" && (
        <Card className="rounded-3xl p-6 border-border/60 glass bg-gradient-to-br from-primary/10 via-transparent to-accent/10" data-testid="earnings-banner">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Ihre Einnahmen</div>
              <div className="font-heading text-5xl font-bold mt-1">€ {earnings.total_earned_expected.toLocaleString('de-DE')}</div>
              <div className="text-sm text-muted-foreground mt-1">
                Bereits abgeschlossen: <b className="text-foreground">€ {earnings.total_earned_done.toLocaleString('de-DE')}</b> · Provisionssatz: {earnings.commission_rate}%
              </div>
            </div>
            <div className="flex gap-4">
              <div className="text-center">
                <div className="font-heading text-3xl font-bold">{earnings.jobs_done}</div>
                <div className="text-xs text-muted-foreground mt-1">Erledigt</div>
              </div>
              <div className="text-center">
                <div className="font-heading text-3xl font-bold text-primary">{earnings.jobs_total - earnings.jobs_done}</div>
                <div className="text-xs text-muted-foreground mt-1">Offen</div>
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
        <Stat label="Einsätze gesamt" value={stats.total_jobs ?? 0} icon={Briefcase} accent="bg-primary/15 text-primary" testid="stat-total-jobs" />
        <Stat label="Offene Aufgaben" value={stats.open_jobs ?? 0} icon={Activity} accent="bg-amber-500/15 text-amber-500" testid="stat-open-jobs" />
        <Stat label="Abgeschlossen" value={stats.done_jobs ?? 0} icon={CheckCircle2} accent="bg-accent/15 text-accent" testid="stat-done" />
        <Stat label="Aktive Mitarbeiter" value={stats.active_users ?? 0} icon={Users} accent="bg-violet-500/15 text-violet-400" testid="stat-users" />
        <Stat label="Einnahmen (€)" value={stats.revenue ?? 0} icon={DollarSign} accent="bg-accent/15 text-accent" testid="stat-revenue" />
        <Stat label="Offen (€)" value={stats.open_amount ?? 0} icon={Receipt} accent="bg-primary/15 text-primary" testid="stat-open-amount" />
        <Stat label="Heute geplant" value={stats.jobs_today ?? 0} icon={Calendar} accent="bg-pink-500/15 text-pink-400" testid="stat-today" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 rounded-3xl p-0 border-border/60 overflow-hidden" data-testid="dashboard-map">
          <div className="p-6 pb-3">
            <h3 className="font-heading text-xl font-semibold">Einsatz-Karte</h3>
            <p className="text-sm text-muted-foreground">Alle aktiven Einsätze im Überblick</p>
          </div>
          <div className="h-[400px]">
            <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                         attribution='&copy; OpenStreetMap' />
              {markers.map(j => (
                <Marker key={j.id} position={[j.lat, j.lng]} icon={icon}>
                  <Popup><b>{j.title}</b><br />{j.address}</Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </Card>

        <Card className="rounded-3xl p-6 border-border/60 glass" data-testid="upcoming-jobs">
          <h3 className="font-heading text-xl font-semibold mb-4">Anstehende Einsätze</h3>
          <div className="space-y-3">
            {jobs.slice(0, 6).map(j => (
              <div key={j.id} className="flex items-start gap-3 p-3 rounded-2xl hover:bg-secondary/60 transition">
                <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
                  <Briefcase className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate text-sm">{j.title}</div>
                  <div className="text-xs text-muted-foreground truncate">{j.address}</div>
                  <div className="text-[10px] uppercase tracking-wider mt-1 text-muted-foreground">{j.status}</div>
                </div>
              </div>
            ))}
            {jobs.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">Noch keine Einsätze.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
